import { useQuery } from "@tanstack/react-query";
import React from "react";
import useApi from "../../shared/hooks/useApi";
import {
  contributeAiCapabilityParamOption,
  declareAiCapability,
  useProvideAiCapability,
} from "../ai-omnibar/ai-capability-registry";
import { declareAiTabDestination } from "../ai-omnibar/ai-tab-destinations";

// The tab saying it is somewhere to go. Service Plans opens on Imports, so this tab is only
// reachable by name if it says so itself.
declareAiTabDestination({
  id: "servicePlans.open",
  title: "Open the Service Plans tab",
  stateName: "app.patients.servicePlans.plans",
  opens: "page:servicePlans",
  description:
    "Opens Patients > Service Plans > Service Plans: the service plans themselves, rather than the imports that created them.",
  location: "the tab bar of Service Plans",
  permissionKey: "view_patient_page_cdsc_import",
  examples: ["open service plans", "draft service plans in Brooklyn"],
});

/**
 * What the service plans list can do for the AI omnibar.
 *
 * `scope: "page"` because these only work while this page is on screen - `app.navigate` declares that
 * it opens that space, so "draft service plans in Brooklyn" comes back as navigate + filter rather
 * than navigate and nothing.
 */
declareAiCapability({
  id: "servicePlans.filter",
  title: "Filter the service plans list",
  effect: "prefill",
  description:
    "Sets the filters on Patients > Service Plans: a free-text search over patient name and id, the plan's status, and the office. The page opens on every plan in the agency, so a status given here narrows it. Only works while that page is open.",
  location: "the filter bar of the Patients > Service Plans page",
  scope: "page:servicePlans",
  permissionKey: "view_patient_page_cdsc_import",
  params: [
    {
      name: "search",
      type: "text",
      required: false,
      searchesFreeText: true,
      description: "Free text the list matches against patient name or id.",
    },
    {
      name: "status",
      type: "enum",
      required: false,
      multiple: true,
      description: "Whether the plan is still a draft or has been finalized.",
      options: [
        { value: "DRAFT", label: "Draft" },
        { value: "FINALIZED", label: "Finalized" },
      ],
    },
    {
      name: "office",
      type: "enum",
      required: false,
      multiple: true,
      description: "Limit to patients in these offices.",
      // Agency data, contributed by the page while it is mounted.
    },
  ],
  examples: [
    "draft service plans",
    "service plans for the europe office",
    "finalized service plans for smith",
  ],
});

export function useServicePlansAiCapabilities(params: {
  /**
   * The page's own setFilter. Typed loosely on purpose: it is generic over that page's query
   * variables, and this hook only ever passes the keys it declares.
   */
  setFilter: (key: string, value: unknown) => void;
}): void {
  const { setFilter } = params;
  const { queries } = useApi();

  const setFilterRef = React.useRef(setFilter);
  setFilterRef.current = setFilter;

  // The same query the filter bar's own office select uses, so this reads its cache rather than
  // fetching anything of its own.
  const offices = useQuery(queries.office.list());
  const officeRows = offices.data?.offices ?? [];
  const officeKey = officeRows.map((office) => `${office.id}:${office.name}`).join("|");

  React.useEffect(() => {
    const releases = officeRows.map((office) =>
      contributeAiCapabilityParamOption("servicePlans.filter", "office", {
        value: String(office.id),
        label: office.name,
      })
    );

    return () => releases.forEach((release) => release());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [officeKey]);

  // Not available until the office list has arrived: an empty option list turns a real filter into a
  // free-text search, so "europe" would land in the search box instead of the office filter.
  useProvideAiCapability(
    "servicePlans.filter",
    (args, context) => {
      const applied: string[] = [];
      const asList = (value: unknown) => (Array.isArray(value) ? value : [value]);
      // The label the option carried, so the line says "Brooklyn" rather than an id.
      const label = (name: string) => context.labels[name] ?? String(args[name]);

      if (args.search !== undefined) {
        setFilterRef.current("search", String(args.search));
        applied.push(`"${String(args.search)}"`);
      }

      if (args.status !== undefined) {
        const statuses = asList(args.status).map((one) => String(one).toUpperCase());

        for (const status of statuses) {
          if (status !== "DRAFT" && status !== "FINALIZED") {
            throw new Error(`A service plan is a draft or finalized, not ${status}.`);
          }
        }

        setFilterRef.current("statuses", statuses);
        applied.push(statuses.map((status) => status.toLowerCase()).join(" and "));
      }

      if (args.office !== undefined) {
        setFilterRef.current(
          "officeIds",
          asList(args.office).map((one) => Number(one))
        );
        applied.push(label("office"));
      }

      return applied.length === 0
        ? "Re-applied the service plan filters"
        : `Showing service plans: ${applied.join(", ")}`;
    },
    { enabled: offices.isSuccess }
  );
}
